import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";

import {
  Container,
  Row,
  Col,
  Button,
  Modal,
  Form,
  ProgressBar,
  Accordion,
  Spinner,
} from "react-bootstrap";

import { useNavigate, useParams, Link } from "react-router-dom";

import { IoArrowBack } from "react-icons/io5";
import { FaPlay } from "react-icons/fa";
// import { FaBook } from "react-icons/fa";
// import { ImAlarm } from "react-icons/im";

import { toast } from "react-toastify";
import { STUDENT_COURSE_API, CHAT_API } from "../../../api/api";

// import VideoIcon from "../../../assets/student/video.jpg";
// import PdfIcon from "../../../assets/student/pdf.png";
// import AssignmentIcon from "../../../assets/student/assignment.png";

// latestIcons
import videoIcon from "../../../assets/icons/student/video.svg";
import pdfIcon from "../../../assets/icons/student/cheat-sheet.svg";
import assignmentIcon from "../../../assets/icons/student/assignment.svg";
import videoColorIcon from "../../../assets/icons/student/videoIconColor.svg";
import lifetime from "../../../assets/icons/student/lifetime-access.svg";
import certificate from "../../../assets/icons/student/certificate-completion1.svg";
import weeksImg from "../../../assets/student/course-detail-weeksIcon.png";
import lessonsImg from "../../../assets/student/course-detail-lessonsIcon.png";
import hours from "../../../assets/icons/student/timer.svg";

import { initialsFromName } from "../../../utils/getInitials";
import { handleApiError } from "../../../utils/apiErrorHandler";

import "./StudentCourseVideoSection.css";

const StudentCourseVideoSection = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);

  const [courseData, setCourseData] = useState(null);
  const [finalAssignment, setFinalAssignment] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [activeKey, setActiveKey] = useState("0");

  const [isPlaying, setIsPlaying] = useState(false);

  const [doubtText, setDoubtText] = useState("");
  const [showDoubtModal, setShowDoubtModal] = useState(false);

  //loading
  const [loadingCourseDetail, setCourseDetailLoading] = useState(true);
  const [loadingDoubt, setLoadingDoubt] = useState(false);

  // main toast error
  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  const fetchCourseData = useCallback(async () => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!userId || !token) {
      setCourseDetailLoading(false);
      navigate("/signin");

      return;
    }

    try {
      const response = await axios.post(
        STUDENT_COURSE_API.GET_STUDENT_COURSE,
        {
          student_id: userId,
          course_id: courseId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const responseData = response.data;

      if (responseData.statusCode === 200) {
        const course = responseData.data;
        setCourseData(course);
        setFinalAssignment(responseData.finalAssignment ?? null);

        let selectedVid = null;
        let selectedMod = null;

        for (const module of course.modules || []) {
          // Find first unlocked but NOT completed
          const incompleteVideo = module.videos?.find(
            (v) => v.is_unlocked && !v.is_completed && v.video_url,
          );

          if (incompleteVideo) {
            selectedVid = incompleteVideo;
            selectedMod = module;
            break;
          }

          // If all completed → keep last unlocked
          const unlockedVideos = module.videos?.filter(
            (v) => v.is_unlocked && v.video_url,
          );

          if (unlockedVideos?.length) {
            selectedVid = unlockedVideos[unlockedVideos.length - 1];
            selectedMod = module;
          }
        }

        setSelectedModule(selectedMod);
        setSelectedVideo(selectedVid);
        setIsPlaying(false);

        // Open correct accordion module
        const moduleIndex = course.modules.findIndex(
          (m) => m.module_id === selectedMod?.module_id,
        );

        if (moduleIndex !== -1) {
          setActiveKey(moduleIndex.toString());
        }
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(responseData.message);
      }
    } catch (error) {
      console.error("Error fetching course videos:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      if (error.response?.status === 404) {
        setTimeout(() => {
          navigate("/student/courses");
        }, 1000);
        return; // ← handle 404 separately
      }
      handleApiError(error, navigate, showErrorToast, "/signin");
    } finally {
      setCourseDetailLoading(false);
    }
  }, [courseId, navigate]);

  useEffect(() => {
    fetchCourseData();
  }, [fetchCourseData]);

  // const handlePlay = () => {
  //   setIsPlaying(true);
  //   videoRef.current?.play();
  // };

  const handlePlay = () => {
    videoRef.current?.play().catch(() => {});
  };

  const handleVideoSelect = (module, video, index) => {
    if (!video.is_unlocked || !video.video_url) {
      return;
    }

    setSelectedModule(module);
    setSelectedVideo(video);
    setActiveKey(index.toString());
    setIsPlaying(false);

    videoRef.current?.pause();
  };

  const handleUpdateProgress = async () => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!token || !userId) {
      navigate("/signin");

      return;
    }

    if (!selectedVideo || !videoRef.current) return;

    const currentTime = videoRef.current.currentTime;
    const h = Math.floor(currentTime / 3600)
      .toString()
      .padStart(2, "0");
    const m = Math.floor((currentTime % 3600) / 60)
      .toString()
      .padStart(2, "0");
    const s = Math.floor(currentTime % 60)
      .toString()
      .padStart(2, "0");
    const watched = `${h}:${m}:${s}`;

    try {
      const response = await axios.post(
        STUDENT_COURSE_API.UPDATE_WATCH_PROGRESS,
        {
          student_id: userId,
          module_video_id: selectedVideo.module_video_id,
          watched,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        // Refetch full course to unlock next video properly
        await fetchCourseData();
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Progress update error:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/signin");
    }
  };

  // Send message
  const handleStudentSendDoubt = async () => {
    if (!doubtText.trim()) {
      showErrorToast("Please enter your doubt.");
      return;
    }

    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!userId || !token) {
      setLoadingDoubt(false);
      navigate("/signin");

      return;
    }

    setLoadingDoubt(true);
    try {
      const response = await axios.post(
        CHAT_API.SEND_MESSAGE,
        {
          user_id: userId,
          course_id: courseId,
          message: doubtText,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        setDoubtText("");
        setShowDoubtModal(false);
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error send doubt:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/signin");
    } finally {
      setLoadingDoubt(false);
    }
  };

  if (loadingCourseDetail) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!courseData) return null;

  // Format "HH:MM:SS" to user-friendly string
  const formatDuration = (duration) => {
    if (!duration) return "0 min";
    const [hours, minutes] = duration.split(":").map(Number); //seconds
    let result = "";
    if (hours > 0) result += `${hours} hr `;
    if (minutes > 0) result += `${minutes} min `;
    // if (seconds > 0) result += `${seconds} sec`;
    if (!hours && !minutes) result = "1 min"; // at least 1 min && !seconds
    return result.trim();
  };

  const progress = courseData.progress?.percentage || 0;
  const totalLessons = courseData.progress?.total_videos || 0;

  return (
    <Container fluid className="student-certificate-section-container">
      <div className="student-dashboard-backmoving-button-wrapper">
        <button
          className="student-dashboard-backmoving-button-main"
          onClick={() => navigate(-1)}
        >
          <IoArrowBack className="student-dashboard-backmoving-button-icon" />
          <span className="student-dashboard-backmoving-button-text">Back</span>
        </button>
      </div>

      <Row>
        {/* LEFT SIDE  */}
        <Col lg={8} md={12} className="student-course-left">
          <p className="student-course-vedio-section-breadcrumb">
            Courses <span> › </span>
            <span
              className="student-course-video-course-breadcrumb-text"
              title={courseData.course_title}
            >
              {courseData.course_title}
            </span>
            <span> › </span>
            <span
              className="student-course-video-course-breadcrumb-text"
              title={selectedModule?.module_title}
            >
              {selectedModule?.module_title || "N/A"}
            </span>
          </p>

          <div className="student-course-vedio-section-card">
            <video
              ref={videoRef}
              className="student-course-vedio-section-video"
              src={selectedVideo?.video_url || ""}
              controls={true}
              controlsList="nodownload"
              disablePictureInPicture
              preload="metadata"
              onPlay={() => setIsPlaying(true)}
              onPause={() => {
                setIsPlaying(false);
                // handleUpdateProgress();
              }}
              onEnded={() => {
                setIsPlaying(false);
                handleUpdateProgress();
              }}
            />

            {!isPlaying && selectedVideo?.video_url && (
              <div
                className="student-course-vedio-section-overlay"
                onClick={handlePlay}
              >
                <div className="student-course-vedio-section-play-btn">
                  <FaPlay />
                </div>
              </div>
            )}
          </div>

          <div className="mt-4">
            <h2
              className="student-course-vedio-section-title"
              title={selectedVideo?.video_title}
            >
              {/* {selectedModule?.module_title || "N/A"} */}
              {selectedVideo?.video_title}
            </h2>

            <p className="student-course-vedio-section-description">
              {selectedModule?.module_description || "N/A"}
            </p>

            <h5 className="student-course-vedio-section-meta-titlesss">
              This Course Includes
            </h5>
            <div className="student-course-vedio-section-meta">
              <span>
                <img src={videoColorIcon} alt="icon" />
                {formatDuration(courseData.total_video_duration)} of recorded
                lessons
              </span>
              <span>
                <img src={lifetime} alt="icon" /> Lifetime access
              </span>
              <span>
                <img src={certificate} alt="icon" /> Certificate of completion
              </span>
            </div>
          </div>

          {/* TUTOR SECTION  */}
          <div className="student-course-tutor-section">
            <div className="student-course-vedio-section-mentor-row">
              <div className="student-course-vedio-section-avatar">
                {initialsFromName(courseData.tutor_name)}
              </div>

              <div className="student-course-vedio-section-mentorinfo">
                <p className="student-course-vedio-section-mentorname">
                  {courseData.tutor_name}
                </p>
                <span className="student-course-vedio-section-mentorrole">
                  {courseData.subject_to_teach || "N/A"}
                </span>
              </div>
            </div>

            <p className="student-course-vedio-section-mentorrole-paraa">
              {courseData.professional_background || "N/A"}
            </p>

            <div className="student-course-action-buttons">
              <Link to={`/student/feedback/${courseId}`}>
                <Button className="student-course-review-btn">
                  Leave Review
                </Button>
              </Link>

              <Button
                className="student-course-doubt-btn"
                onClick={() => setShowDoubtModal(true)}
              >
                Ask Doubts
              </Button>
            </div>
          </div>
        </Col>

        {/* RIGHT SIDE  */}
        <Col lg={4} md={12} className="student-course-right">
          <div className="student-course-sidebar">
            <div className="student-course-progress">
              <span>Your Progress: {progress}%</span>
              <ProgressBar now={progress} />
            </div>

            <div className="student-course-content-header">
              <h6>Course Content</h6>
              <p>
                <img
                  src={weeksImg}
                  alt="Weeks"
                  className="student-course-content-header-img"
                />
                {courseData.no_of_modules} sections
                <img
                  src={lessonsImg}
                  alt="Lessons"
                  className="student-course-content-header-img"
                />
                {totalLessons} lessons
                <img
                  src={hours}
                  alt="Hours"
                  className="student-course-content-header-img"
                />
                {formatDuration(courseData.total_video_duration)}
                {/* total length */}
              </p>
            </div>

            <Accordion
              activeKey={activeKey}
              onSelect={(key) => setActiveKey(key)}
              className="student-course-accordion"
            >
              {courseData.modules?.map((module, index) => (
                <Accordion.Item
                  eventKey={index.toString()}
                  key={module.module_id}
                >
                  <Accordion.Header>{module.module_title}</Accordion.Header>

                  <Accordion.Body>
                    {module.videos?.map((video) => (
                      <div
                        key={video.module_video_id}
                        className={`student-course-lesson-item ${
                          !video.is_unlocked ? "locked" : ""
                        }`}
                        onClick={() => handleVideoSelect(module, video, index)}
                        title={
                          !video.is_unlocked
                            ? "Complete previous video to unlock"
                            : ""
                        }
                      >
                        <img
                          src={videoIcon}
                          alt={video.video_title}
                          className="student-course-lesson-icon"
                        />

                        <span
                          className="student-course-section-video-title"
                          title={video.video_title}
                        >
                          {video.video_title}
                        </span>
                      </div>
                    ))}

                    {module.sheet_file_url && (
                      <a
                        href={module.sheet_file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="student-course-lesson-item"
                      >
                        <img
                          src={pdfIcon}
                          alt="pdf"
                          className="student-course-lesson-icon"
                        />

                        <span>
                          {module.sheet_file
                            ?.split("/")
                            .pop()
                            .split("_")
                            .slice(1)
                            .join("_")}
                        </span>
                      </a>
                    )}

                    {module.assignment && (
                      <div
                        className={`student-course-lesson-item ${
                          !module.assignment.is_unlocked ? "locked" : ""
                        }`}
                        title={
                          !module.assignment.is_unlocked
                            ? "Complete previous videos to unlock this assignment"
                            : ""
                        }
                        onClick={() => {
                          if (!module.assignment.is_unlocked) return;
                          navigate(
                            `/student/assignment-detail/${module.assignment.assignment_id}`,
                          );
                        }}
                      >
                        <img
                          src={assignmentIcon}
                          alt="assignment"
                          className="student-course-lesson-icon"
                        />
                        <span
                          className="student-course-section-video-title"
                          title={module.assignment.assignment_title}
                        >
                          {module.assignment.assignment_title}
                        </span>
                      </div>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              ))}

              {/* FINAL ASSIGNMENT (ONLY ONCE) */}
              {finalAssignment && (
                <Accordion.Item eventKey="final">
                  <Accordion.Header>Final Assignment</Accordion.Header>

                  <Accordion.Body>
                    <div
                      className={`student-course-lesson-item ${
                        !finalAssignment.is_unlocked ? "locked" : ""
                      }`}
                      title={
                        !finalAssignment.is_unlocked
                          ? "Complete all modules to unlock final assignment"
                          : ""
                      }
                      onClick={() => {
                        if (!finalAssignment.is_unlocked) return;

                        navigate(
                          `/student/final-assignment/${finalAssignment.final_assignment_id}`,
                        );
                      }}
                    >
                      <img
                        src={assignmentIcon}
                        alt="final assignment"
                        className="student-course-lesson-icon"
                      />

                      <span>{finalAssignment.assignment_title}</span>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              )}
            </Accordion>
          </div>
        </Col>
      </Row>

      {/* <Row className="pt-3 pb-3">
        <Col lg={8} md={12}></Col>
      </Row> */}

      <Modal
        show={showDoubtModal}
        onHide={() => setShowDoubtModal(false)}
        centered
        backdrop="static"
        className="student-course-doubt-modal"
      >
        <Modal.Header closeButton className="student-course-doubt-modal-header">
          <Modal.Title className="student-course-doubt-modal-title">
            Ask Your Doubt
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="student-course-doubt-modal-body">
          <p className="student-course-doubt-modal-desc">
            Have a question about this course? Ask your mentor and get the
            guidance you need.
          </p>

          <Form>
            <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Type your doubt here..."
                className="student-course-doubt-textarea"
                value={doubtText}
                onChange={(e) => setDoubtText(e.target.value)}
              />
            </Form.Group>

            <div className="text-end">
              <Button
                variant="primary"
                className="student-course-doubt-submit-btn"
                onClick={handleStudentSendDoubt}
                disabled={loadingDoubt}
              >
                {loadingDoubt ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  "Send Doubt"
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default StudentCourseVideoSection;
